import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useCurrency } from '../../contexts/CurrencyContext';

/**
 * Landing Page - Emphase sur "Essaie avec ton URL"
 * ✅ Enregistre aussi un draft dans localStorage comme le wizard (pendingAnalysis)
 */
const PENDING_KEY = 'pendingAnalysis';

const LandingPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { theme, toggleTheme } = useTheme();

  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');

  // Données statiques pour le graphique de projection 10 ans (Valeur + Cashflow cumulatif)
  const projectionData = useMemo(() => ([
    { year: 0, value: 485000, cashflow: 0 },
    { year: 1, value: 502000, cashflow: 22400 },
    { year: 2, value: 520000, cashflow: 45800 },
    { year: 3, value: 538000, cashflow: 70200 },
    { year: 4, value: 557000, cashflow: 95600 },
    { year: 5, value: 576000, cashflow: 122100 },
    { year: 6, value: 596000, cashflow: 149700 },
    { year: 7, value: 617000, cashflow: 178500 },
    { year: 8, value: 639000, cashflow: 208500 },
    { year: 9, value: 661000, cashflow: 239800 },
    { year: 10, value: 682000, cashflow: 272400 }
  ]), []);

  // Indicateurs de confiance statiques (pas de stats utilisateur)
  const trustIndicators = useMemo(() => ([
    { label: '100 % québécois', sub: 'Conçu au Québec' },
    { label: 'Données en CAD', sub: 'Prix et revenus $' },
    { label: 'Plus rapide qu’Excel', sub: '2–3 minutes' }
  ]), []);

  // Exemple de données d'analyse réaliste pour la démo visuelle
  const exampleAnalysis = useMemo(() => ({
    address: "1245 Rue Saint-Jean, Québec",
    city: "Québec, QC",
    price: 875000,
    units: 6,
    propertyType: "6-plex",
    yearBuilt: 1985,
    cashflow: 1847,
    capRate: 7.8,
    roi: 18.4,
    dscr: 1.52,
    score: 82
  }), []);

  const exampleKPIs = useMemo(() => ([
    { label: 'Cashflow mensuel', value: '+1 847$', icon: 'TrendingUp', color: '#22c55e' },
    { label: 'ROI Cash-on-Cash', value: '18.4%', icon: 'Target', color: '#3b82f6' },
    { label: 'Taux de capitalisation', value: '7.8%', icon: 'PieChart', color: '#8b5cf6' },
    { label: 'Ratio couverture dette', value: '1.52x', icon: 'Shield', color: '#f59e0b' }
  ]), []);

  // Image d'immeuble par défaut
  const defaultBuildingImage = "/traditional_quebec_6plex.png";

  const normalizeUrl = (v) => (v || '').trim();

  const isValidUrl = (v) => {
    try {
      const u = new URL(v);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const savePendingAnalysis = (propertyUrl) => {
    // ✅ Même clé que dans PropertyAnalysisWizard: pendingAnalysis
    // On met juste ce qu’on a (le wizard merge déjà ses defaults de formData)
    const payload = {
      stage: 'analysis',
      propertyUrl: propertyUrl
    };

    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
    } catch (e) {
      // pas bloquant
      console.error('Failed to save pending analysis from landing:', e);
    }
  };

  const handleTryUrl = () => {
    const cleaned = normalizeUrl(urlInput);

    if (!cleaned) {
      setUrlError("Colle l’URL de la fiche (ex: Centris).");
      return;
    }
    if (!isValidUrl(cleaned)) {
      setUrlError("URL invalide. Exemple: https://www.centris.ca/...");
      return;
    }

    setUrlError('');
    savePendingAnalysis(cleaned);

    // ✅ On passe aussi l’URL en query param (pratique si tu veux auto-import au mount du wizard)
    navigate(`/property-analysis-wizard?url=${encodeURIComponent(cleaned)}`);
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-3">
          <img src="/immoiq-logo.png" alt="ImmoIQ" style={{ height: '36px', width: 'auto' }} />
          <span className="text-2xl font-bold text-foreground">ImmoIQ</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '0.8125rem',
              fontWeight: 500
            }}
            className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-transparent hover:border-border"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            aria-label={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
          >
            <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} />
            <span>{theme === 'dark' ? 'Clair' : 'Sombre'}</span>
          </button>

          <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
            Se connecter
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left */}
        <div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 text-foreground">
            Colle l’URL d’un immeuble. <span className="text-primary">ImmoIQ s’occupe du reste.</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground mb-6 leading-relaxed">
            Une analyse qui prenait 15 minutes dans Excel se fait maintenant en <span className="text-foreground font-semibold">2–3 minutes</span>.
            Sans calculs. Sans copier-coller. Juste des chiffres pour décider.
          </p>

          {/* URL input - HERO */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Essaie avec l’URL d’un immeuble
            </label>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <input
                  value={urlInput}
                  onChange={(e) => {
                    setUrlInput(e.target.value);
                    if (urlError) setUrlError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTryUrl();
                  }}
                  type="url"
                  placeholder="https://www.centris.ca/..."
                  className="w-full h-12 px-4 rounded-xl border border-border bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/40"
                />
                {urlError && (
                  <div className="mt-2 text-sm text-red-600">
                    {urlError}
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={handleTryUrl}
                className="h-12 px-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-0 justify-center"
              >
                Analyser cet immeuble
              </Button>
            </div>

            <div className="mt-3 text-sm text-muted-foreground">
              Gratuit · Aucun calcul requis · Résultats instantanés
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap gap-8 mt-10 pt-8 border-t border-border">
            {trustIndicators.map((item, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-foreground">{item.label}</div>
                <div className="text-sm text-muted-foreground">{item.sub}</div>
              </div>
            ))}
          </div>

          {/* Secondary CTA */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/property-analysis-wizard')}
              className="rounded-xl"
            >
              Ouvrir l’analyse manuelle
            </Button>

            <Button
              variant="ghost"
              size="lg"
              onClick={() => navigate('/subscription-pricing')}
              className="rounded-xl"
            >
              Voir les forfaits
            </Button>
          </div>
        </div>

        {/* Right: Visual example */}
        <div style={{
          background: 'var(--color-card)',
          borderRadius: '20px',
          border: '1px solid var(--color-border)',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
        }}>
          {/* Image */}
          <div style={{ height: '220px', position: 'relative', overflow: 'hidden' }}>
            <img
              src={defaultBuildingImage}
              alt="Immeuble à revenus"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0, left: 0, right: 0,
              height: '120px',
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)'
            }} />

            {/* Property overlay */}
            <div style={{ position: 'absolute', bottom: '1rem', left: '1rem', right: '1rem' }}>
              <div className="rounded-xl p-4 shadow-md bg-white dark:bg-card border border-border">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div className="text-base font-semibold text-foreground">
                      {exampleAnalysis.address}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <span>{exampleAnalysis.propertyType}</span>
                      <span>•</span>
                      <span>{exampleAnalysis.units} unités</span>
                      <span>•</span>
                      <span className="font-semibold text-foreground">
                        {formatCurrency(exampleAnalysis.price)}
                      </span>
                    </div>
                  </div>
                  <div style={{
                    background: exampleAnalysis.score >= 80 ? '#22c55e' : exampleAnalysis.score >= 60 ? '#f59e0b' : '#ef4444',
                    color: 'white',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', lineHeight: 1, opacity: 0.9 }}>Score</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 800, lineHeight: 1.1 }}>{exampleAnalysis.score}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* KPI grid */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {exampleKPIs.map((kpi, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-border bg-background p-4"
                >
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name={kpi.icon} size={16} />
                    <span>{kpi.label}</span>
                  </div>
                  <div className="mt-2 text-xl font-bold text-foreground">
                    {kpi.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div className="mt-6 rounded-xl border border-border bg-background p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-foreground">
                  Projection 10 ans (démo)
                </div>
                <div className="text-xs text-muted-foreground">
                  Valeur + cashflow cumulatif
                </div>
              </div>

              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={projectionData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'value') return [formatCurrency(value), 'Valeur'];
                        if (name === 'cashflow') return [formatCurrency(value), 'Cashflow cumulatif'];
                        return [value, name];
                      }}
                      labelFormatter={(label) => `Année ${label}`}
                    />
                    <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} />
                    <Area type="monotone" dataKey="cashflow" stroke="#22c55e" fill="#22c55e" fillOpacity={0.12} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Mini promise */}
            <div className="mt-5 text-sm text-muted-foreground">
              Pas besoin de comprendre toutes les formules — tu colles un lien, tu vois les chiffres.
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            Comment ça marche (vraiment)
          </h2>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-border bg-background p-5">
              <div className="flex items-center gap-2 text-foreground font-semibold">
                <Icon name="Link2" size={18} />
                <span>1. Colle l’URL</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Centris, DuProprio ou toute fiche publique.
              </p>
            </div>

            <div className="rounded-xl border border-border bg-background p-5">
              <div className="flex items-center gap-2 text-foreground font-semibold">
                <Icon name="Sparkles" size={18} />
                <span>2. ImmoIQ remplit</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Prix, loyers, taxes, dépenses… et tu ajustes si besoin.
              </p>
            </div>

            <div className="rounded-xl border border-border bg-background p-5">
              <div className="flex items-center gap-2 text-foreground font-semibold">
                <Icon name="CheckCircle" size={18} />
                <span>3. Tu décides</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Cashflow, rendement, projections et verdict clair.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground mb-2">
                Essaie maintenant avec un immeuble réel
              </div>
              <input
                value={urlInput}
                onChange={(e) => {
                  setUrlInput(e.target.value);
                  if (urlError) setUrlError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTryUrl();
                }}
                type="url"
                placeholder="https://www.centris.ca/..."
                className="w-full h-12 px-4 rounded-xl border border-border bg-background text-foreground outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={handleTryUrl}
              className="h-12 px-6 rounded-xl justify-center"
            >
              Analyser cet immeuble
            </Button>
          </div>

          {urlError && (
            <div className="mt-3 text-sm text-red-600">
              {urlError}
            </div>
          )}

          <div className="mt-3 text-sm text-muted-foreground">
            Gratuit · Aucun calcul requis · Résultats instantanés
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} ImmoIQ — outil d’analyse pour immeubles à revenus.
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
              Se connecter
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/subscription-pricing')}>
              Forfaits
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
